import {
  INTRANET_MAX_PINNED,
  IntranetContentKind,
  type IntranetCategory,
} from '@gb-ticket/shared-types';
import { SettingsIcon } from 'lucide-react';
import { Link, useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { IntranetColumns } from '@/products/intranet/components/intranet-columns';
import { IntranetFeedColumn } from '@/products/intranet/components/intranet-feed';
import { useIntranetHomeWriteHref } from '@/products/intranet/features/intranet/use-intranet-home-write-href';
import {
  useIntranetCategories,
  useIntranetContents,
  useIntranetFeedStream,
  useJoinIntranetCategory,
  useLeaveIntranetCategory,
} from '@/products/intranet/features/intranet/use-intranet';
import { intranetCategorySettingsPath } from '@/products/intranet/intranet-paths';
import { CategorySettingsPage } from '@/products/intranet/pages/category-settings-page';

/** Start page: channels, news feed, Schnellzugriff placeholder (D6). */
export function IntranetHomePage() {
  usePageTitle('Intranet');
  const categories = useIntranetCategories();
  const [params] = useSearchParams();
  const activeSlug = params.get('category');
  const saved = params.get('saved') === 'true';
  const settings = params.get('einstellungen') === '1';
  const category = categories.data?.items.find((item) => item.slug === activeSlug);
  const categoryMissing = Boolean(activeSlug) && categories.isSuccess && !category;
  const preview = category?.canRead === false;
  const writeHref = useIntranetHomeWriteHref();
  const feedReady = (!activeSlug || Boolean(category)) && !preview;
  const filter = {
    kind: IntranetContentKind.NEWS,
    categoryId: category?.id,
    saved,
    page: 1,
  } as const;
  const pins = useIntranetContents(
    { ...filter, pinned: true, pageSize: INTRANET_MAX_PINNED },
    feedReady,
  );
  const stream = useIntranetFeedStream(
    { kind: filter.kind, categoryId: filter.categoryId, saved: filter.saved },
    feedReady,
  );
  const streamItems = stream.data?.pages.flatMap((page) => page.items) ?? [];
  return (
    <IntranetColumns>
      {settings && category ? (
        <CategorySettingsPage category={category} />
      ) : (
        <>
          {category && !saved ? (
            <>
              <ChannelHeading category={category} />
            </>
          ) : null}
          <IntranetFeedColumn
            pinned={categoryMissing || preview ? [] : (pins.data?.items ?? [])}
            stream={categoryMissing || preview ? [] : streamItems}
            pending={
              !categoryMissing &&
              !preview &&
              (pins.isPending || (stream.isPending && !stream.data) || categories.isPending)
            }
            error={pins.isError || (stream.isError && !stream.data)}
            writeHref={writeHref}
            saved={saved}
            hasMore={Boolean(stream.hasNextPage)}
            loadingMore={stream.isFetchingNextPage}
            loadMoreError={stream.isFetchNextPageError}
            onLoadMore={() => {
              if (!stream.isFetchingNextPage) {
                void stream.fetchNextPage();
              }
            }}
          />
        </>
      )}
    </IntranetColumns>
  );
}

function ChannelHeading({ category }: { category: IntranetCategory }) {
  const join = useJoinIntranetCategory();
  const leave = useLeaveIntranetCategory();
  const description = category.description?.trim() ? category.description : null;
  if (!category.canManage && !description && !category.canJoin && !category.canLeave) {
    return null;
  }

  async function onJoin() {
    try {
      await join.mutateAsync(category.id);
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError ? error.message : 'Beitreten ist fehlgeschlagen.',
      );
    }
  }

  async function onLeave() {
    try {
      await leave.mutateAsync(category.id);
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError ? error.message : 'Verlassen ist fehlgeschlagen.',
      );
    }
  }

  return (
    <div className="mb-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="min-w-0">
            <h1 className="truncate text-xl font-semibold">{category.name}</h1>
            {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
          </div>
        </div>
        <div className="flex shrink-0 gap-2">
          {category.canJoin ? (
            <Button type="button" size="sm" onClick={() => void onJoin()} disabled={join.isPending}>
              Beitreten
            </Button>
          ) : null}
          {category.canLeave ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => void onLeave()}
              disabled={leave.isPending}
            >
              Verlassen
            </Button>
          ) : null}
          {category.canManage ? (
            <Button asChild variant="outline" size="sm">
              <Link to={intranetCategorySettingsPath(category.slug)}>
                <SettingsIcon aria-hidden />
                Einstellungen
              </Link>
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
